import React from 'react';
import { motion } from 'framer-motion';
import { Home, RefreshCw, AlertTriangle, Mail, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

interface ErrorPageProps {
  errorCode?: number;
  title?: string;
  message?: string;
  showRedirect?: boolean;
}

const ErrorPage: React.FC<ErrorPageProps> = ({ 
  errorCode = 404, 
  title = "Page Not Found",
  message = "The page you're looking for doesn't exist or has been moved.",
  showRedirect = true
}) => {
  const commonErrors = {
    404: {
      title: "Page Not Found",
      message: "The page you're looking for doesn't exist or has been moved.",
      suggestion: "Check the URL or navigate back to our homepage."
    },
    500: {
      title: "Server Error",
      message: "Something went wrong on our end. We're working to fix it.",
      suggestion: "Please try again in a few moments."
    },
    403: {
      title: "Access Denied",
      message: "You don't have permission to access this resource.",
      suggestion: "Contact support if you believe this is an error."
    },
    503: {
      title: "Service Unavailable",
      message: "The service is temporarily unavailable for maintenance.",
      suggestion: "We'll be back shortly. Thank you for your patience."
    }
  };

  const errorInfo = commonErrors[errorCode as keyof typeof commonErrors] || {
    title,
    message,
    suggestion: "Please try again or contact support if the problem persists."
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-950 to-zinc-900 pt-32 pb-20 container mx-auto px-6 max-w-4xl">
      {/* Animated Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(3)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-[500px] h-[500px] rounded-full bg-primary/5 blur-3xl"
            initial={{ 
              x: Math.random() * 100 - 50,
              y: Math.random() * 100 - 50 
            }}
            animate={{
              x: Math.random() * 100 - 50,
              y: Math.random() * 100 - 50,
            }}
            transition={{
              duration: 10 + i * 2,
              repeat: Infinity,
              repeatType: "reverse"
            }}
          />
        ))}
      </div>

      {/* Error Content */}
      <div className="relative z-10">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          {/* Error Badge */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center space-x-3 bg-zinc-900/80 backdrop-blur-sm border border-red-500/20 px-6 py-3 rounded-full mb-8"
          >
            <AlertTriangle className="w-5 h-5 text-red-500" />
            <span className="text-sm font-bold tracking-widest text-zinc-400 uppercase">
              Error {errorCode}
            </span>
          </motion.div>

          {/* Error Code Display */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="mb-8"
          >
            <h1 className="text-8xl md:text-9xl font-display font-bold bg-gradient-to-r from-red-500 to-orange-500 bg-clip-text text-transparent tracking-tighter leading-none">
              {errorCode}
            </h1>
          </motion.div>

          {/* Main Message */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="mb-8"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{errorInfo.title}</h2>
            <p className="text-xl text-zinc-400 max-w-2xl mx-auto leading-relaxed">
              {errorInfo.message}
            </p>
          </motion.div>

          {/* Suggestion */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mb-12"
          >
            <p className="text-zinc-500 max-w-lg mx-auto">
              {errorInfo.suggestion}
            </p>
          </motion.div>
        </motion.div>

        {/* Action Buttons */}
        {showRedirect && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-16"
          >
            <Link to="/">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="group bg-gradient-to-r from-primary to-accent text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-3 hover:shadow-lg hover:shadow-primary/20 transition-shadow"
              >
                <Home className="w-5 h-5" />
                <span>Back to Homepage</span>
              </motion.button>
            </Link>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => window.location.reload()}
              className="group bg-zinc-800/50 backdrop-blur-sm border border-white/10 text-white px-8 py-4 rounded-full font-semibold flex items-center space-x-3 hover:border-primary/50 transition-colors"
            >
              <RefreshCw className="w-5 h-5 group-hover:rotate-180 transition-transform duration-500" />
              <span>Reload Page</span>
            </motion.button>
          </motion.div>
        )}

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12"
        >
          <QuickLink
            icon={<ArrowLeft className="w-5 h-5" />}
            title="Go Back"
            action="Return to previous page"
            onClick={() => window.history.back()}
          />
          <QuickLink
            icon={<Mail className="w-5 h-5" />}
            title="Contact Support"
            action="Get help from our team"
            link="mailto:support@duostacksystems.com"
          />
          <QuickLink
            icon={<Home className="w-5 h-5" />}
            title="Explore Services"
            action="View our offerings"
            link="/services"
          />
        </motion.div>

        {/* Error Illustration */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="mt-20 relative"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 to-transparent pointer-events-none" />
          <div className="relative max-w-md mx-auto">
            <div className="relative">
              {/* Circuit-like design */}
              <svg className="w-full h-auto" viewBox="0 0 400 200" fill="none">
                <path
                  d="M50,100 Q200,50 350,100"
                  stroke="url(#gradient1)"
                  strokeWidth="2"
                  strokeDasharray="10,5"
                  className="animate-pulse"
                />
                <path
                  d="M50,150 Q200,200 350,150"
                  stroke="url(#gradient2)"
                  strokeWidth="2"
                  strokeDasharray="5,5"
                  className="animate-pulse"
                />
                <circle cx="200" cy="100" r="8" fill="#EF4444" className="animate-ping" />
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#EF4444" stopOpacity="0" />
                    <stop offset="50%" stopColor="#EF4444" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#EF4444" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#F97316" stopOpacity="0" />
                    <stop offset="50%" stopColor="#F97316" stopOpacity="0.5" />
                    <stop offset="100%" stopColor="#F97316" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* Floating elements */}
              {[...Array(5)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute w-2 h-2 bg-red-500 rounded-full"
                  initial={{ 
                    x: Math.random() * 350,
                    y: Math.random() * 180,
                    opacity: 0.5 
                  }}
                  animate={{
                    y: [null, Math.random() * 20 - 10],
                    opacity: [0.5, 1, 0.5]
                  }}
                  transition={{
                    duration: 2 + i,
                    repeat: Infinity,
                    repeatType: "reverse",
                    delay: i * 0.2
                  }}
                />
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

interface QuickLinkProps {
  icon: React.ReactNode;
  title: string;
  action: string;
  onClick?: () => void;
  link?: string;
}

const QuickLink: React.FC<QuickLinkProps> = ({ 
  icon, 
  title, 
  action, 
  onClick, 
  link 
}) => {
  const content = (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="bg-zinc-900/50 backdrop-blur-sm border border-white/5 rounded-2xl p-6 text-center hover:border-primary/30 transition-all cursor-pointer group"
    >
      <div className="text-primary mb-3 flex justify-center group-hover:scale-110 transition-transform">
        {icon}
      </div>
      <h4 className="font-bold text-white mb-1">{title}</h4>
      <p className="text-sm text-zinc-500">{action}</p>
    </motion.div>
  );

  if (link) {
    return (
      <Link to={link}>
        {content}
      </Link>
    );
  }

  return (
    <div onClick={onClick}>
      {content}
    </div>
  );
};

// Export default and named export for specific error pages
export const Error404: React.FC = () => <ErrorPage errorCode={404} />;
export const Error500: React.FC = () => <ErrorPage errorCode={500} />;
export const Error403: React.FC = () => <ErrorPage errorCode={403} />;

export default ErrorPage;